import { Table } from 'antd'
import { SortOrder } from 'antd/lib/table'
import { FC, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PerEmpty } from '../../../../components/custom_components/Empty'
import { PerTableActionButtons } from '../../../../components/custom_components/TableActionButtons'
import PerText from '../../../../components/custom_components/Text'
import { useAuth } from '../../../../context/auth-context'
import { useDateTime } from '../../../../shared/useDateTime'
import { Country, ActiveHrBusinessPartnerDto } from '../../../../types'
import { useHrSupportStore } from '../HrSupportStore'
import shallow from 'zustand/shallow'
import { PerAvatarPlus } from '../../../../components/custom_components/AvatarPlus'
import { NameDisplayPreference } from '../../../../graphql/enum/name-display-preference.enum'
import { computeFullname } from '../../../../shared/nameDisplayPreferencesUtil'
import { DeleteHRBPRelationModal } from './DeleteHRBPRelationModal'
import { deleteHRBPRelation } from '../../../../graphql/hrBusinessPartners/MutationFunctions'
import { showNotification } from '../../../../components/custom_components/Notification/notification'

interface Props {
  hrbpList: ActiveHrBusinessPartnerDto[]
}

export const HrSupportHRBPTable: FC<Props> = ({ hrbpList }) => {
  const { t } = useTranslation()
  const { user } = useAuth()
  const { formatDateShort } = useDateTime(user)
  const [selected] = useHrSupportStore((state) => [state.selected, state.feedbackTabFilters], shallow)
  const [modalData, setModalData] = useState<ActiveHrBusinessPartnerDto | undefined>(undefined)
  const [deletionInProgress, setDeletionInProgress] = useState<boolean>(false)
  const [deletionError, setDeletionError] = useState<string | undefined>(undefined)

  const onDeleteModalOpen = (rowData: ActiveHrBusinessPartnerDto) => {
    setModalData(rowData)
  }

  const onDeleteModalCancel = () => {
    setModalData(undefined)
    setDeletionError(undefined)
  }

  const handleDeleteHRBPRelation = async (rowData: ActiveHrBusinessPartnerDto) => {
    if (selected && !!selected.value) {
      const country: Country = selected.value as Country

      setDeletionInProgress(true)
      const res = await deleteHRBPRelation(rowData.id, country)

      if (!res.errors) {
        setModalData(undefined)
        setDeletionError(undefined)
        showNotification({
          type: 'success',
          message: t(`companySettingsView.hrBusinessPartnersTab.table.notifications.delete.success.title`),
          description: t(`companySettingsView.hrBusinessPartnersTab.table.notifications.delete.success.description`, {
            employeeFirstName: rowData.employee.firstName,
            companyName: rowData.companyName,
          }),
        })
      } else {
        try {
          // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
          setDeletionError(res.errors[0].extensions!.validationErrors[0].message)
        } catch (e) {
          setDeletionError('')
        }
      }
      setDeletionInProgress(false)
    }
  }

  const columns = [
    {
      key: 'employee',
      title: t('companySettingsView.hrBusinessPartnersTab.table.employee'),
      width: '35%',
      defaultSortOrder: 'ascend' as SortOrder,
      sorter: (rowA: ActiveHrBusinessPartnerDto, rowB: ActiveHrBusinessPartnerDto) => {
        const nameA = computeFullname(user.nameDisplayPreference, rowA.employee.firstName, rowA.employee.lastName)
        const nameB = computeFullname(user.nameDisplayPreference, rowB.employee.firstName, rowB.employee.lastName)
        return nameA.localeCompare(nameB)
      },
      render: (rowData: ActiveHrBusinessPartnerDto) => (
        <PerAvatarPlus
          nameDisplayPreference={user.nameDisplayPreference as NameDisplayPreference}
          employeeId={rowData.employee.id}
          firstName={rowData.employee.firstName}
          lastName={rowData.employee.lastName}
          infoText={
            <>
              {rowData.employee.companyName} - {rowData.employee.buName}
            </>
          }
        />
      ),
    },
    {
      key: 'company',
      width: '35%',
      title: t('companySettingsView.hrBusinessPartnersTab.table.companyName'),
      sorter: (rowA: ActiveHrBusinessPartnerDto, rowB: ActiveHrBusinessPartnerDto) => {
        return rowA.companyName.localeCompare(rowB.companyName)
      },
      render: (data: ActiveHrBusinessPartnerDto) => <PerText>{data.companyName}</PerText>,
    },
    {
      key: 'since',
      width: '20%',
      title: t('companySettingsView.hrBusinessPartnersTab.table.fromDate'),
      sorter: (fromA: ActiveHrBusinessPartnerDto, fromB: ActiveHrBusinessPartnerDto) => {
        return fromA.fromDate.localeCompare(fromB.fromDate)
      },
      render: (rowData: ActiveHrBusinessPartnerDto) => {
        return <PerText>{formatDateShort(rowData.fromDate)}</PerText>
      },
    },
    {
      key: 'actions',
      width: '10%',
      title: t('companySettingsView.hrBusinessPartnersTab.table.actions'),
      render: (rowData: ActiveHrBusinessPartnerDto) => {
        return (
          <PerTableActionButtons
            variant="pastel"
            justify="start"
            buttons={[
              {
                icon: 'delete_fill',
                color: 'error',
                tooltip: t('companySettingsView.hrBusinessPartnersTab.table.delete'),
                onClick: () => {
                  onDeleteModalOpen(rowData)
                },
              },
            ]}
          />
        )
      },
    },
  ]

  return (
    <>
      {!!modalData && (
        <DeleteHRBPRelationModal
          relation={modalData}
          onClose={() => onDeleteModalCancel()}
          onSubmit={async () => {
            await handleDeleteHRBPRelation(modalData)
          }}
          deletionInProgress={deletionInProgress}
          deletionError={deletionError}
        />
      )}
      <Table
        className={'light small scroll-compensation viewWithTabsAndInfoBox'}
        dataSource={hrbpList.map((o) => ({ ...o, key: o.id }))}
        columns={columns}
        rowClassName={() => 'minHeight-row'}
        pagination={false}
        scroll={{ y: true }}
        locale={{
          emptyText: <PerEmpty heading={t('companySettingsView.hrBusinessPartnersTab.table.emptyTable')} icon />,
        }}
      />
    </>
  )
}
